// Unit insignia: the squadron badge a pilot picks in the hangar and wears
// beside the callsign on every surface that names them — the nameplate over
// the plane, the kill feed, chat, the scoreboard, the killcam's "shot down
// by", the round's TOP GUNS, the Hall of Fame. The server keeps the choice on
// the career (internal/game/insignia.go) and hands it round on the roster and
// the join/"insignia" events; the catalog rides /config.json ("insignia": id,
// name, motto) in picker order.
//
// The art is one small SVG per id in client/insignia/, drawn from the source
// in art/insignia/. `new URL(..., import.meta.url)` lets Vite fingerprint each
// badge into assets/ like the models (never inlined — forty badges baked into
// game.js would be dead weight for a pilot who meets three), and resolves the
// same way unbundled, where it lands on the raw /insignia/<id>.svg.

// Ids are plain on purpose: they drop into a URL path and an HTML attribute
// with no escaping, and anything else is a badge this build doesn't carry.
const ID_RE = /^[a-z0-9]{1,16}$/;

let catalog = [];
const byId = new Map();

/** Install the catalog from config.insignia. Unknown shapes are dropped. */
export function setInsigniaCatalog(list) {
  catalog = Array.isArray(list) ? list.filter((d) => d && ID_RE.test(d.id ?? "")) : [];
  byId.clear();
  for (const d of catalog) byId.set(d.id, d);
}

export const insigniaCatalog = () => catalog;

/** The catalog entry for an id, or null for "" / unknown. */
export const insigniaDef = (id) => byId.get(id) ?? null;

/** URL of a badge's SVG, or "" when the id isn't in the catalog. */
export function insigniaURL(id) {
  if (!byId.has(id)) return "";
  return new URL(`../insignia/${id}.svg`, import.meta.url).href;
}

const escAttr = (s) => String(s).replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`);

/** An inline <img> for text lines (feed, chat, scoreboard), or "" for no
 *  badge. Sized by CSS (.ins) in em so it rides the line's own font. */
export function insigniaHTML(id, cls = "ins") {
  const url = insigniaURL(id);
  if (!url) return "";
  return `<img class="${cls}" src="${url}" alt="" title="${escAttr(byId.get(id).name)}" draggable="false">`;
}

// Decoded images for the canvas-drawn nameplates, one per badge. A plate
// asks for its badge; if it isn't decoded yet the plate draws without it and
// is told to redraw once it is, so nothing ever waits on the network.
const images = new Map(); // id -> { img, ready, waiters }

/** The decoded badge image, or null while it loads (onReady then fires once)
 *  or when there's no badge / no DOM. */
export function insigniaImage(id, onReady = null) {
  const url = insigniaURL(id);
  if (!url || typeof Image === "undefined") return null;
  let e = images.get(id);
  if (!e) {
    const img = new Image();
    e = { img, ready: false, waiters: [] };
    images.set(id, e);
    img.decoding = "async";
    img.onload = () => {
      e.ready = true;
      for (const fn of e.waiters.splice(0)) fn();
    };
    img.onerror = () => {
      e.waiters.length = 0; // a missing badge just leaves the plate plain
    };
    img.src = url;
  }
  if (e.ready) return e.img;
  if (onReady) e.waiters.push(onReady);
  return null;
}
